import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Carousel } from "react-responsive-carousel";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Dialog,
  IconButton,
  Slide,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { SubmitButton } from "src/components/Button/SubmitButton";
import { EmployeeWorkingTimeCarousel } from "src/components/EmployeeWorkingTimeCarousel/EmployeeWorkingTimeCarousel";
import { useAppSelector } from "src/hooks/redux";
import { getCategoryTemplates } from "src/store/reducers/categoryTemplatesSlice";
import {
  getEmployees,
  inviteEmployees,
  updateEmployees,
} from "src/store/reducers/employeesSlice";
import { getStudios  } from "src/store/reducers/studiosSlice";
import { getNormalizedWorkingTimes } from "src/utils/calendar";
import { generalStyles } from "src/utils/generalStyles/generalStyles";
import { getNormalizedWorkplaces, validateWorkingTimes } from "src/utils/helpers";
import { InfoString } from "src/utils/infoString";
import { isEmail , isName } from 'src/utils/validation';

import {
  CloseIcon,
} from "../../../Icons";
import {
  closeEmployeesDialog,
  resetEmployeesDialogParams,
} from "../../../store/reducers/dialogSlice";
import {
  ScreensList,
  setEmptyStateScreen,
} from "../../../store/reducers/emptyScreensSlice";
import { closeModal, showModal } from "../../../store/reducers/modalSlice";
import { EmployeeSkillService } from "../../EmployeeSkillService/EmployeeSkillService";
import { AvatarSelect } from "../../ImageSelect/AvatarSelect";
import { DefaultInput } from "../../Input/DefaultInput";
import { DefaultArraySelector } from "../../Selector/DefaultArraySelector";
import { Steppler } from "../../Steppler/Steppler";

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DisplayError = memo(({workingDate}:any) => {
  return (
    <Box
      sx={{
        display:
           workingDate?.length > 0
             ? "block"
             : "none",
        width: "100%",
        backgroundColor: "#D51F29",
        padding: "5px 0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "761px",
          margin: "0 auto",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
        }}
      >
        <InfoIcon
          sx={{
            width: "15.5px",
            height: "15.5px",
            color: "#FFFFFF",
            marginRight: "6px",
          }}
        />
        The set date is not valid.
      </Box>
    </Box>
  );
});

const steps = [
  {
    title: "Basic information",
  },
  {
    title: "Working time",
  },
  {
    title: "Skill & service",
  },
];

export const EmployeesDialog = ({ isOpen }: any) => {
  const dispatch = useDispatch();

  const { employeesDialogParams: params } = useSelector(
    (state: any) => state.dialog
  );

  const {  isInviteRequestLoading, isUpdateRequestLoading} = useAppSelector((state) => state.employees)

  const { studios } = useAppSelector((state: any) => state.studios);
  const studiosToSelect = useMemo(() => {
    return studios
      .filter((studio: any) => studio?.schedule?.workingTimes?.length > 0)
      .map(({ id, title, images }: any) => {
        return {
          id,
          name: title,
          surname: "",
          imageSrc: images?.[0],
        };
      });
  },[studios])

  const [activeStep, setActiveStep] = useState<number>(params.step);
  const [workingDate, setWorkingDate] = useState<any>();
  const [maxAvailableStepToClick, setMaxAvailableStepToClick] =
    useState<number>(params.maxAvailableStep);
  const formRef = useRef<any>();
  const avatarRef = useRef<any>();

  // Step 1 values
  const [photo, setPhoto] = useState<string>(params.data.photo);
  const [firstName, setFirstName] = useState(params.data.firstName);
  const [lastName, setLastName] = useState(params.data.lastName);
  const [email, setEmail] = useState<string>(params.data.email);
  const [assignedStudios, setAssignedStudios] = useState<any>(
    (params.data.workplaces &&
      params.data.workplaces
        .map(({ studioId }: any) => studioId)
        .filter((id: any) =>
          studios.some((studio: any) => studio.id === id)
        )) ||
      (params.data.studios &&
        params.data.studios
          .map(({ id }: any) => id)
          .filter((id: any) => studios.some((studio: any) => studio.id === id))) || []
  );

  // Step 2 values
  const studiosTabsRefs = useRef([]);
  const [dateChanged, setDateChanged] = useState<string>('');
  const [dateSelectorChanged, setDateSelectorChanged] = useState<string>('');
  const [weekChanged, setWeekChanged] = useState<any>([]);
  const [isWorkingTimeStepFulfilled, setIsWorkingTimeStepFulfilled] =
    useState(false);

  // Step 3 values
  const { services: servicesList } = useSelector(
    (state: any) => state.serviceTemplates
  );

  const newServicesList = useMemo(() => {
    return servicesList.map(
      (service: { id: string; title: string }) => ({
        id: `${service.id}`,
        label: service.title,
      })
    );
  },[servicesList])

  const getStudioInfoWithService = (skills: any[], studios: any[]) => {
    const result = [];
    const studioMap = new Map();

    for (const skill of skills) {
      const skillServiceId = skill.service.id;
      for (const studio of studios) {
        const hasService = studio.services.some((service: { id: any; }) => service.id === skillServiceId);

        if (hasService) {
          if (studioMap.has(studio.id)) {
            const existingStudio = result[studioMap.get(studio.id)];
            existingStudio.skills.push({
              service: skill.service.id,
              rank: skill.rank,
              specialty: skill.specialty
            });
          } else {
            const newStudioInfo = {
              studioId: studio.id,
              skills: [{
                service: skill.service.id,
                rank: skill.rank,
                specialty: skill.specialty
              }]
            };
            studioMap.set(studio.id, result.length);
            result.push(newStudioInfo);
          }
        }
      }
    }
    return result;
  }

  const studiosTabsArray = useMemo(() => {
    return studios
      ?.filter(({ id }: any) =>
        assignedStudios?.find((studioId: any) => id === studioId)
      )
      .map((studio: any) => {
        const masterSchedule = params?.data?.studios?.find(
          ({ id }: any) => id === studio?.id
        )?.masterSchedule;

        return {
          ...studio,
          masterSchedule,
        };
      });
  },[
    assignedStudios,
    params,
    studios,
  ])

  const [loadedExistingServices, setLoadedExistingServices] = useState<any[]>(
    getStudioInfoWithService(params.data.skills || [], studios) || []
  );

  const handleExistingServicesChange = useCallback((newWorkplace: any) => {
    const id = newWorkplace.studioId;
    const newSkills = newWorkplace.skills;

    const newExistingServices = JSON.parse(
      JSON.stringify(loadedExistingServices)
    );
    const index = newExistingServices.findIndex(
      (item: { studioId: any }) => item.studioId === id
    );
    if (index !== -1) {
      newExistingServices[index].skills = newSkills;
    } else {
      newExistingServices.push({ studioId: id, skills: newSkills });
    }
    setLoadedExistingServices(newExistingServices);
  },[loadedExistingServices]);

  const handleChangeDate = useCallback((newValue:string) => {
    setDateChanged(newValue);
  },[])

  const handleChangeDateSelector = useCallback((newValue:any) => {
    setDateSelectorChanged(newValue);
  },[])

  const handleAddServiceButton = useCallback((id: any) => {
    const existingServiceIndex = loadedExistingServices.findIndex(
      (service) => service.studioId === id
    );

    if (existingServiceIndex !== -1) {
      const updatedExistingServices = [...loadedExistingServices];
      updatedExistingServices[existingServiceIndex].skills.push({
        service: "",
        rank: "none",
        specialty: "" ,
      });
      setLoadedExistingServices(updatedExistingServices);
    } else {
      const newService = {
        studioId: id,
        skills: [{ service: "", rank: "none", specialty: "" }],
      };
      setLoadedExistingServices([...loadedExistingServices, newService]);
    }
  },[loadedExistingServices]);

  const handleChangeName = useCallback((newValue: string) => {
    setFirstName(newValue);
  },[]);

  const handleChangeLastName = useCallback((newValue: string) => {
    setLastName(newValue);
  },[]);

  const handleChangeEmail = useCallback((newValue: string) => {
    setEmail(newValue);
  },[]);

  const handleChangePhoto = useCallback((newImage: string) => {
    setPhoto(newImage);
  },[]);

  const onWeekdaysChange = useCallback((newWeekdays?: any) => {
    const isFulfilled = studiosTabsRefs?.current?.every((el: any) => {
      const daysPeriods = el?.workingTimesRef?.current?.getDaysPeriods();

      if (daysPeriods === undefined) {
        return true;
      }
      return Object.keys(daysPeriods)?.length > 0;
    });
    setWeekChanged(newWeekdays)
    setIsWorkingTimeStepFulfilled(isFulfilled);
  }, [studiosTabsRefs]);

  const handleChangeSelectStudio = useCallback((value: any) => {
    setAssignedStudios(value);
  },[]);

  const isContinueButtonAvailable = useMemo(() => {
    const isBasicInfoStepFulfilled =
      isName(firstName) &&
      isName(lastName) &&
      isEmail(email) &&
      photo != "" &&
      assignedStudios.length > 0;

    const isSkillsStepFulfilled =
      loadedExistingServices
        .filter(
          obj => studiosTabsArray.map((obj: { id: any; }) => obj.id).includes(obj.studioId)
        ).length === studiosTabsArray?.length &&
      loadedExistingServices?.every(
        (obj) =>
          obj.skills.length > 0 &&
          obj.skills.every(
            (skill: { service: any }) =>
              skill.service !== null && skill.service !== 0
          )
      );

    const isScheduleValid = workingDate?.length === 0;

    if (activeStep === 0) {
      return isBasicInfoStepFulfilled;
    } else if (activeStep === 1) {
      return (isWorkingTimeStepFulfilled && isScheduleValid)
    } else if (activeStep === 2) {
      return (
        isSkillsStepFulfilled &&
        isWorkingTimeStepFulfilled &&
        isBasicInfoStepFulfilled
      );
    } else {
      return true;
    }
  }, [
    firstName,
    email,
    photo,
    lastName,
    assignedStudios,
    activeStep,
    isWorkingTimeStepFulfilled,
    loadedExistingServices,
    studiosTabsArray,
    workingDate
  ]);

  const isSaveButtonValid = useMemo(() => {
    return isContinueButtonAvailable;
  }, [isContinueButtonAvailable]);

  const handleChangeStep = (nextStep: number) => {
    nextStep === 1 && onWeekdaysChange();
    setActiveStep(nextStep);
  };

  const handleContinueButtonPress = () => {
    onWeekdaysChange();
    setActiveStep((oldStep) => oldStep + 1);
    setMaxAvailableStepToClick(activeStep + 1);
  };

  const resetDialogData = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setAssignedStudios([]);
    setActiveStep(0);
    setMaxAvailableStepToClick(0);

    formRef?.current?.reset();
    avatarRef?.current?.deleteImage();
  };

  const onClose = () => {
    resetDialogData();
    dispatch(resetEmployeesDialogParams());
    dispatch(closeEmployeesDialog());
  };

  useEffect(() => {
    if(activeStep === 1 && studiosTabsRefs?.current?.length > 0) {
      const pickedWorkingTimes = getNormalizedWorkplaces(studiosTabsArray, studiosTabsRefs);

      const periodsComparisonArray = validateWorkingTimes(
        pickedWorkingTimes,
        studiosTabsArray,
      );

      const workingDateIssue = periodsComparisonArray?.filter(
        (date: any) => !date.isPeriodDateValid ||
          date.weekdaysValidArray.some(({isValid}: any) => isValid === false)
      );

      setWorkingDate(workingDateIssue)
    }
  },[dateChanged, dateSelectorChanged, activeStep, weekChanged, studiosTabsRefs, studiosTabsArray])


  const handleSaveButtonPress = () => {
    // @ts-ignore
    const normalizedWorkplaces = studiosTabsArray.map(({ id }, index) => {
      // @ts-ignore
      const { workingTimesRef, rulesRef } = studiosTabsRefs?.current[index];

      const normalizedWorkingTimes = getNormalizedWorkingTimes([
        {
          // @ts-ignore
          daysPeriods: workingTimesRef?.current?.getDaysPeriods(),
          // @ts-ignore
          period: rulesRef?.current?.getPeriod(),
        },
      ]);

      const respectiveSkills = loadedExistingServices.filter((service => +service.studioId === +id))[0]?.skills;

      return {
        studioId: +id,
        skills: respectiveSkills || [],
        schedule: {
          workingTimes: normalizedWorkingTimes,
          daysOff: [],
        },
        daysOff: [],
      };
    });

    if (params.isEdit) {
      dispatch(
        updateEmployees({
          employeeId: params.data.id,
          firstName:
            params.data.firstName !== firstName ? firstName : undefined,
          lastName: params.data.lastName !== lastName ? lastName : undefined,
          photo: params.data.photo !== photo ? photo : undefined,
          workplaces: normalizedWorkplaces,
          onSuccess: () => {

            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: "Changes successfully saved",
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
            dispatch(getEmployees())
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    } else {
      dispatch(
        inviteEmployees({
          firstName,
          lastName,
          photo,
          email,
          workplaces: normalizedWorkplaces,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: "Employee successfully added",
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(
                    setEmptyStateScreen({
                      key: ScreensList.EMPLOYEES,
                      value: false,
                    })
                  );
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }
  };

  useEffect(() => {
    dispatch(getStudios());
    dispatch(getCategoryTemplates());
  }, []);


  useEffect(() => {
    // todo little hack for scroll to top after every change active step carousel
    //@ts-ignore
    window.document.querySelector('.MuiPaper-root')?.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: "100%",
          backgroundColor: "#fafafa",
          padding: "16px 40px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            marginBottom: "16px",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#F3F3F3",
              color: "#676767",
              fontSize: "26px",
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: "600",
              }}
            >
              {params.isEdit ? "Edit employee" : "Add new employee"}
            </Typography>
          </Box>

          {activeStep < steps.length - 1 ? (
            <SubmitButton
              title="Continue"
              disabled={!isContinueButtonAvailable}
              onClick={handleContinueButtonPress}
            />
          ) : (
            <SubmitButton
              title={params.isEdit ? "Save changes" : "Create employee"}
              isLoading={isInviteRequestLoading || isUpdateRequestLoading}
              disabled={!isSaveButtonValid || isInviteRequestLoading || isUpdateRequestLoading}
              onClick={handleSaveButtonPress}
            />
          )}
        </Box>

        <Steppler
          steps={steps}
          maxAvailableStepToClick={maxAvailableStepToClick}
          activeStep={activeStep}
          onStepChange={handleChangeStep}
        />
      </Box>
      <DisplayError workingDate={workingDate}/>
      <Carousel
        selectedItem={activeStep}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        showThumbs={false}
        transitionTime={120}
      >
        {/* Step 1 = Basic Information*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "96px 16px 0",
              width: "761px",
              minWidth: "400px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "48px",
                }}
              >
                Basic information
              </Typography>
              <AvatarSelect
                ref={avatarRef}
                image={photo}
                onImageChanged={handleChangePhoto}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#828282",
                  marginTop: "16px",
                  marginBottom: "40px",
                }}
              >
                Allowed file types
                : png, jpg, jpeg.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <form ref={formRef}>
                <DefaultInput
                  initialValue={firstName}
                  placeholder="name"
                  label="Name"
                  onInputValueChange={handleChangeName}
                  validationFn={isName}
                  sx={generalStyles.borderTopRightLeftRadius}
                />
                <DefaultInput
                  initialValue={lastName}
                  placeholder="surname"
                  label="Surname"
                  onInputValueChange={handleChangeLastName}
                  validationFn={ isName}
                  sx={generalStyles.borderTopNone}
                />
                <DefaultInput
                  initialValue={email}
                  placeholder="email"
                  label="Email"
                  onInputValueChange={handleChangeEmail}
                  validationFn={isEmail}
                  sx={generalStyles.borderBottomLeftRightRadius}
                />
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginTop: "56px",
                    marginBottom: "24px",
                  }}
                >
                  Workspace
                </Typography>

                <DefaultArraySelector
                  valuesArray={assignedStudios}
                  valuesToSelect={studiosToSelect}
                  onChanged={handleChangeSelectStudio}
                  placeholder="studio"
                  title="Studio"
                  sx={generalStyles.borderRadiusAll}
                />

                <InfoString child="You have to choose at least 1 studio"/>

              </form>
            </Box>
          </Box>
        </Box>
        {/* Step 2 = Working time Information*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "656px",
              minWidth: "400px",
              marginTop: "32px",
              flexDirection: "column",
            }}
          >
            <EmployeeWorkingTimeCarousel
              handleChangeDateSelector={handleChangeDateSelector}
              handleChangeDate={handleChangeDate}
              onWeekdaysChange={onWeekdaysChange}
              refs={studiosTabsRefs}
              studiosSelected={studiosTabsArray}
            />
          </Box>
        </Box>
        {/* Step 3 = Skills Information*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "0 16px 0",
              width: "761px",
              minWidth: "400px",
              marginTop: "32px",
            }}
          >
            <EmployeeSkillService
              handleAddServiceButton={handleAddServiceButton}
              handleExistingServicesChange={handleExistingServicesChange}
              loadedExistingServices={loadedExistingServices}
              studiosSelected={studiosTabsArray}
              newServicesList={newServicesList}
            />
          </Box>
        </Box>
      </Carousel>
    </Dialog>
  );
};
